import {
  AlertCard,
  Card,
  IconWrapper,
  Link,
  Loader,
  Tag,
} from "@caprikorn/dynamo-ui";
import {
  classNames,
  getFullAddress,
  getFullName,
} from "@caprikorn/dynamo-utils";
import type { FC } from "react";
import { useParams } from "react-router-dom";

import { OverviewGroupCard } from "@components/OverviewGroupCard/OverviewGroupCard";
import { CircleInfo, Elec, Gas, Phone } from "@icons";
import type { UrlParam } from "@router/router.types";
import { useGetConsumption } from "@services/apiEcms/useGetConsumption";
import { useGetExpositionPoint } from "@services/apiEcms/useGetExpositionPoint";
import { getTagStatusColor } from "@utils/miscellaneous.utils";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./PerimeterSiteOverview.module.css";
import type { PerimeterSiteOverviewProps } from "./PerimeterSiteOverview.types";

export const PerimeterSiteOverview: FC<PerimeterSiteOverviewProps> = ({
  overviewData,
  isOverviewLoading,
}) => {
  const { t } = useCustomTranslation(["main", "common"]);
  const {
    customerId = "",
    contractId = "",
    siteId = "",
    energy = "ELEC",
  } = useParams<UrlParam>();

  const { data } = useGetExpositionPoint({
    customerId,
    contractId,
    siteId,
    readingPage: 1,
  });

  const {
    data: consumption,
    isError: isConsumptionError,
    isLoading: isConsumptionLoading,
  } = useGetConsumption({
    energy,
    enabled: true,
    customerId,
    contractId,
    siteId,
  });

  return (
    <div className={classes.container}>
      <OverviewGroupCard />
      <div className={classes.infosCards}>
        <Card
          className={classes.card}
          data-testid="siteDetailOverviewContract-TitledCard"
        >
          {isOverviewLoading ? (
            <Loader />
          ) : (
            <div className={classes.container}>
              <div className={classes.title}>
                <IconWrapper size="sm" src={energy === "ELEC" ? Elec : Gas} />
                <h2 className="font-title-sm">
                  {t("perimeter.overview.site.title", { energy })}
                </h2>
              </div>

              {overviewData?.siteInformation.podName && (
                <h3 className="font-title-md">
                  {overviewData?.siteInformation.podName}
                </h3>
              )}
              {overviewData?.siteInformation.status?.name && (
                <Tag
                  color={getTagStatusColor(
                    overviewData.siteInformation.status.code,
                  )}
                  label={overviewData.siteInformation.status.name}
                  size="sm"
                />
              )}

              <ul>
                <li className={classes.list}>
                  <p className="font-label-default">
                    {overviewData?.siteInformation.energy === "GAZ"
                      ? t("perimeter.overview.site.fields.siteId.GAS")
                      : t("perimeter.overview.site.fields.siteId.ELEC")}
                  </p>
                  <p
                    className={classNames("font-label-highlight", classes.item)}
                  >
                    {overviewData?.siteInformation?.siteId}
                  </p>
                </li>

                <li className={classes.list}>
                  <p className="font-label-default">
                    {t("perimeter.overview.site.fields.address")}
                  </p>
                  <p
                    className={classNames("font-label-highlight", classes.item)}
                  >
                    {overviewData?.consumptionAddress
                      ? getFullAddress(overviewData.consumptionAddress)
                      : t("missingData", { ns: "common" })}
                  </p>
                </li>

                <li className={classes.list}>
                  <p className="font-label-default">
                    {t("perimeter.overview.site.fields.supplyStartDate")}
                  </p>
                  <p
                    className={classNames("font-label-highlight", classes.item)}
                  >
                    {overviewData?.siteInformation?.supplyStartDate
                      ? t("date", {
                          ns: "common",
                          date: new Date(
                            overviewData.siteInformation.supplyStartDate,
                          ),
                        })
                      : t("missingData", { ns: "common" })}
                  </p>
                </li>

                {Boolean(overviewData?.siteInformation.supplyEndDate) && (
                  <li className={classes.list}>
                    <p className="font-label-default">
                      {t("perimeter.overview.site.fields.supplyEndDate")}
                    </p>
                    <p
                      className={classNames(
                        "font-label-highlight",
                        classes.item,
                      )}
                    >
                      {overviewData?.siteInformation.supplyEndDate
                        ? t("date", {
                            ns: "common",
                            date: new Date(
                              overviewData.siteInformation.supplyEndDate,
                            ),
                          })
                        : t("missingData", { ns: "common" })}
                    </p>
                  </li>
                )}

                {Boolean(
                  data?.distributorInformation?.name &&
                  data?.distributorInformation?.number,
                ) && (
                  <li className={classes.list}>
                    <p className="font-label-default">
                      {t("perimeter.overview.site.fields.distributorName", {
                        distributor: data?.distributorInformation.name,
                      })}
                    </p>
                    <p
                      className={classNames(
                        "font-label-highlight",
                        classes.item,
                      )}
                    >
                      {data?.distributorInformation.number}
                    </p>
                  </li>
                )}

                {data?.registrationNumber !== "999" &&
                  data?.registrationNumber !== "000" && (
                    <li className={classes.list}>
                      <p className="font-label-default">
                        {t("perimeter.overview.site.fields.registrationNumber")}
                      </p>
                      <p
                        className={classNames(
                          "font-label-highlight",
                          classes.item,
                        )}
                      >
                        {data?.registrationNumber}
                      </p>
                    </li>
                  )}
              </ul>
            </div>
          )}
        </Card>

        {!isConsumptionError ? (
          <Card
            className={classes.card}
            data-testid="siteDetailOverviewConso-TitledCard"
          >
            {isConsumptionLoading ? (
              <Loader />
            ) : (
              <div className={classes.container}>
                <div className={classes.title}>
                  <IconWrapper size="sm" src={CircleInfo} />
                  <h2 className="font-title-sm">
                    {t("perimeter.overview.consumption.title")}
                  </h2>
                </div>

                <ul>
                  <li className={classes.list}>
                    <p className="font-label-default">
                      {t("perimeter.overview.consumption.fields.previousMonth")}
                    </p>
                    <p
                      className={classNames(
                        "font-label-highlight",
                        classes.item,
                      )}
                    >
                      {consumption?.data.previousMonthInKwh
                        ? t("numberWithUnit", {
                            ns: "common",
                            value: consumption.data.previousMonthInKwh,
                            measurementUnit: "kWh",
                          })
                        : t("unavailable", { ns: "common" })}
                    </p>
                  </li>

                  <li className={classes.list}>
                    <p className="font-label-default">
                      {t("perimeter.overview.consumption.fields.currentYear")}
                    </p>
                    <p
                      className={classNames(
                        "font-label-highlight",
                        classes.item,
                      )}
                    >
                      {consumption?.data.currentYearInKwh
                        ? t("numberWithUnit", {
                            ns: "common",
                            value: consumption.data.currentYearInKwh,
                            measurementUnit: "kWh",
                          })
                        : t("unavailable", { ns: "common" })}
                    </p>
                  </li>
                </ul>
              </div>
            )}
          </Card>
        ) : (
          <AlertCard className={classes.alertCard} variant="warning">
            {t("perimeter.unavailableConsumptionData")}
          </AlertCard>
        )}

        <Card
          className={classes.card}
          data-testid="siteDetailOverviewContact-TitledCard"
        >
          {isOverviewLoading ? (
            <Loader />
          ) : (
            <div className={classes.container}>
              <div className={classes.title}>
                <IconWrapper size="sm" src={Phone} />
                <h2 className="font-title-sm">
                  {t("perimeter.overview.contact.title")}
                </h2>
              </div>

              <ul>
                {Boolean(overviewData?.consumptionContact?.lastName) && (
                  <li className={classes.list}>
                    <p className="font-label-default">
                      {t("perimeter.overview.contact.fields.name")}
                    </p>
                    <p
                      className={classNames(
                        "font-label-highlight",
                        classes.item,
                      )}
                    >
                      {`${overviewData?.consumptionContact.civility} ${getFullName({ firstName: overviewData?.consumptionContact.firstName, lastName: overviewData?.consumptionContact.lastName })}`}
                    </p>
                  </li>
                )}

                {Boolean(overviewData?.consumptionContact.phone) && (
                  <li className={classes.list}>
                    <p className="font-label-default">
                      {t("perimeter.overview.contact.fields.email")}
                    </p>
                    <Link
                      className={classNames(
                        "font-label-highlight",
                        classes.item,
                      )}
                      hasNoDecoration
                      href={`mailto:${overviewData?.consumptionContact.email}`}
                    >
                      {overviewData?.consumptionContact.email}
                    </Link>
                  </li>
                )}

                {Boolean(overviewData?.consumptionContact.phone) && (
                  <li className={classes.list}>
                    <p className="font-label-default">
                      {t("perimeter.overview.contact.fields.phone")}
                    </p>
                    <p
                      className={classNames(
                        "font-label-highlight",
                        classes.item,
                      )}
                    >
                      {overviewData?.consumptionContact.phone}
                    </p>
                  </li>
                )}

                {Boolean(overviewData?.consumptionContact.mobilePhone) && (
                  <li className={classes.list}>
                    <p className="font-label-default">
                      {t("perimeter.overview.contact.fields.mobilePhone")}
                    </p>
                    <p
                      className={classNames(
                        "font-label-highlight",
                        classes.item,
                      )}
                    >
                      {overviewData?.consumptionContact.mobilePhone}
                    </p>
                  </li>
                )}
              </ul>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};
